import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarDays, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { runGeneration } from "@/lib/generate";
import { GeneratingOverlay } from "@/components/GeneratingOverlay";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AGE_GROUPS, LANGUAGES, THEMES, label } from "@/lib/activityData";

export default function WeeklyPlanner() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ age_group: "", theme: "", goals: "", language: "bilingual", num_days: "5" });
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async () => {
    if (!form.age_group || !form.theme) { toast.error(lang === "es" ? "Elige grupo de edad y tema." : "Choose an age group and theme."); return; }
    setLoading(true); setElapsed(0);
    try {
      const j = await runGeneration("weekly-plan", { ...form, num_days: parseInt(form.num_days, 10) }, { onTick: setElapsed });
      toast.success(lang === "es" ? "Plan semanal guardado." : "Weekly plan saved.");
      navigate(`/weekly-plans/${j.result_id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <GeneratingOverlay open={loading} elapsed={elapsed} />
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm font-semibold" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> {t("nav.dashboard")}
        </button>
        <div className="space-y-2">
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">{lang === "es" ? "Planificador Semanal Bilingüe" : "Weekly Bilingual Planner"}</h1>
          <p className="text-muted-foreground text-lg">{lang === "es" ? "Genera un plan realista de lunes a viernes sobre un tema." : "Generate a realistic Monday–Friday plan around one theme."}</p>
        </div>
        <div className="space-y-6 rounded-3xl border border-border bg-card p-6 sm:p-8">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("generator.ageGroup")} <span className="text-secondary">*</span></Label>
              <Select value={form.age_group} onValueChange={set("age_group")}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="wp-age"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{AGE_GROUPS.map((a) => <SelectItem key={a.value} value={a.value}>{label(a, lang)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{lang === "es" ? "Número de días" : "Number of days"}</Label>
              <Select value={form.num_days} onValueChange={set("num_days")}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="wp-days"><SelectValue /></SelectTrigger>
                <SelectContent>{["3", "4", "5"].map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("generator.theme")} <span className="text-secondary">*</span></Label>
            <Input value={form.theme} onChange={set("theme")} placeholder={t("generator.themePlaceholder")} className="h-12 rounded-xl" data-testid="wp-theme" />
            <div className="flex flex-wrap gap-2 pt-1">
              {THEMES.slice(0, 10).map((th, i) => (
                <button key={i} onClick={() => setForm((f) => ({ ...f, theme: label(th, lang) }))} className="rounded-full bg-muted hover:bg-muted/70 px-3 py-1 text-sm text-muted-foreground">{label(th, lang)}</button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>{lang === "es" ? "Metas de aprendizaje (opcional)" : "Learning goals (optional)"}</Label>
            <Textarea rows={2} value={form.goals} onChange={set("goals")} className="rounded-xl resize-none" data-testid="wp-goals" />
          </div>
          <div className="space-y-2">
            <Label>{t("generator.language")}</Label>
            <Select value={form.language} onValueChange={set("language")}>
              <SelectTrigger className="h-12 rounded-xl" data-testid="wp-lang"><SelectValue /></SelectTrigger>
              <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{label(l, lang)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={submit} disabled={loading} className="w-full py-3 rounded-xl bg-secondary hover:bg-secondary/90 text-base" data-testid="wp-submit">
            {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> {t("generator.generating")}</> : <><CalendarDays className="h-5 w-5" /> {t("common.generate")}</>}
          </Button>
        </div>
      </main>
    </div>
  );
}
